/**
 * Firebase Auth Error Helper
 * Handles Firebase authentication error codes and returns user-friendly translation keys
 */

import { ErrorResponse, FirebaseError } from '@handover/shared-types';

export type AuthError = ErrorResponse | FirebaseError;

/**
 * Type guard to check if error is an ErrorResponse
 */
export const isErrorResponse = (error: AuthError): error is ErrorResponse => {
  return 'data' in error && error.data !== undefined;
};

/**
 * Type guard to check if error is a FirebaseError
 */
export const isFirebaseError = (error: AuthError): error is FirebaseError => {
  return 'code' in error && typeof error.code === 'string' && error.code.startsWith('auth/');
};

/**
 * Maps Firebase authentication error codes to translation keys
 * @param error - Firebase error object
 * @returns Translation key for user-friendly error message
 */
export const getAuthErrorMessageKey = (error: FirebaseError): string => {
  if (!error.code) {
    return 'auth.signin.error.invalid.credential';
  }

  switch (error.code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'auth.signin.error.invalid.credential';

    case 'auth/too-many-requests':
      return 'auth.signin.error.too.many.requests';

    case 'auth/user-disabled':
      return 'auth.signin.error.user.disabled';

    case 'auth/invalid-verification-code':
    case 'auth/code-expired':
    case 'auth/missing-verification-code':
    case 'auth/missing-verification-id':
      return 'auth.signin.error.invalid.credential';

    case 'auth/captcha-check-failed':
      return 'auth.signin.error.network';

    case 'auth/network-request-failed':
      return 'auth.signin.error.network';

    default:
      return 'auth.signin.error.invalid.credential';
  }
};

export const getTwoFactorErrorMessage = (
  error: FirebaseError,
  fallbackMessage: string
): string => {
  switch (error.code) {
    case 'auth/invalid-phone-number':
      return 'Select the correct country code and enter a valid mobile number.';
    case 'auth/unverified-email':
      return 'Verify your email address before enabling SMS two-factor authentication.';
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
    case 'auth/missing-verification-id':
      return 'The 6-digit verification code was not accepted. Please try again.';
    case 'auth/code-expired':
      return 'That verification code has expired. Request a new code and try again.';
    case 'auth/captcha-check-failed':
      return 'We could not verify the security check. Please try again.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'This account already has the maximum number of second factors enrolled.';
    case 'auth/second-factor-already-in-use':
      return 'That phone number is already being used as a second factor on another account.';
    case 'auth/multi-factor-info-not-found':
      return 'No SMS two-factor method is enrolled for this account.';
    case 'auth/unsupported-first-factor':
      return 'This account must sign in with email and password before SMS two-factor can be used.';
    case 'auth/requires-recent-login':
      return 'Sign out and sign back in, then try this security change again.';
    case 'auth/quota-exceeded':
    case 'auth/too-many-requests':
      return 'Too many verification attempts were made. Please wait a few minutes and try again.';
    default:
      return fallbackMessage;
  }
};
